// Titles and descriptions for search results, built to one set of rules:
//
//   * A title is at most 60 characters, so Google shows all of it. The brand is appended
//     only when it still fits; Google usually shows the site name on its own line anyway.
//   * A description is at most 160 characters and leads with the answer. On a state or
//     county page that means CDC's own record, so the result itself answers "are there
//     Lone Star ticks in <place>?" before anyone clicks.
//
// src/lib/seo.test.ts runs every state and county through these and checks the limits.

import { SITE_NAME } from "./site";
import type { RiskLevel } from "./risk";
import type { CdcCountyStatus } from "./county-status-text";

export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 160;

const BRAND_SUFFIX = ` | ${SITE_NAME}`;

/** A title with the brand appended only when the whole fits in TITLE_MAX. */
export function pageTitle(core: string): string {
  const bare = core.replace(/(\s*\|\s*LoneStarMap)+\s*$/i, "").trim();
  return bare.length + BRAND_SUFFIX.length <= TITLE_MAX ? `${bare}${BRAND_SUFFIX}` : bare;
}

/** The first candidate that fits, or the last one if none does. */
function firstFitting(candidates: string[], max: number): string {
  return candidates.find((c) => c.length <= max) ?? candidates[candidates.length - 1];
}

/**
 * A description held to DESCRIPTION_MAX: whole sentences where possible, so a long
 * summary is cut at a full stop rather than mid-word.
 */
export function metaDescription(text: string, max = DESCRIPTION_MAX): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max + 1);
  const stop = cut.lastIndexOf(". ");
  if (stop >= 70) return cut.slice(0, stop + 1);
  const space = cut.lastIndexOf(" ", max - 1);
  return `${cut.slice(0, space > 0 ? space : max - 1).replace(/[,;:]$/, "")}…`;
}

/** "March – October" -> "March–October": shorter, and still read as a range. */
function tightRange(season: string): string {
  return season.replace(/\s*–\s*/g, "–");
}

/** "Peak season: March–October." or, for Florida, "Active year-round, peak April–August." */
function seasonSentence(peakSeason: string): string {
  const yearRound = /^year-round \(peak (.+)\)$/i.exec(peakSeason.trim());
  return yearRound
    ? `Active year-round, peak ${tightRange(yearRound[1])}.`
    : `Peak season: ${tightRange(peakSeason)}.`;
}

/** "the District of Columbia"; every state name reads without an article. */
function placeName(name: string): string {
  return name === "District of Columbia" ? `the ${name}` : name;
}

export interface PlaceCounts {
  established: number;
  reported: number;
  total: number;
}

/** Title for a state page: the place first, then what the page has. */
export function stateTitle(stateName: string, level: RiskLevel): string {
  // D.C. is one county, so "County Map" would promise more than the page has.
  if (stateName === "District of Columbia")
    return pageTitle(`Lone Star Ticks in ${placeName(stateName)}`);
  if (level === "low") {
    return pageTitle(`Are There Lone Star Ticks in ${stateName}?`);
  }
  return pageTitle(
    firstFitting(
      [
        `Lone Star Ticks in ${stateName}: County Map & Season`,
        `Lone Star Ticks in ${stateName}: Map & Season`,
        `Lone Star Ticks in ${stateName}`,
      ],
      TITLE_MAX,
    ),
  );
}

/** Description for a state page, from CDC's county tally and the state's season. */
export function stateDescription(opts: {
  stateName: string;
  level: RiskLevel;
  peakSeason: string;
  counts: PlaceCounts | null;
  /** "counties" or "parishes". */
  nouns: string;
}): string {
  const { stateName: s, level, counts, nouns } = opts;
  const season = seasonSentence(opts.peakSeason);
  if (level === "low" || !counts) {
    const reports =
      counts && counts.reported > 0
        ? ` CDC has reports from ${counts.reported} of its ${counts.total} ${nouns} but lists none where they are established.`
        : " CDC lists no county where they are established.";
    return metaDescription(
      firstFitting(
        [
          `Lone Star ticks are rarely reported in ${placeName(s)}.${reports} Other ticks live here, so the same prevention applies.`,
          `Lone Star ticks are rarely reported in ${placeName(s)}.${reports}`,
          `Lone Star ticks are rarely reported in ${placeName(s)}. Other ticks live here, so the same prevention applies.`,
        ],
        DESCRIPTION_MAX,
      ),
    );
  }
  const tally =
    counts.total === 1
      ? counts.established
        ? `CDC lists Lone Star ticks as established in ${placeName(s)}`
        : `CDC has Lone Star tick reports from ${placeName(s)}`
      : counts.established > 0
        ? `CDC lists Lone Star ticks as established in ${counts.established} of ${s}'s ${counts.total} ${nouns}`
        : `CDC has Lone Star tick reports from ${counts.reported} of ${s}'s ${counts.total} ${nouns}`;
  const lead = level === "emerging" ? `Lone Star ticks are spreading into ${s}. ` : "";
  return metaDescription(
    firstFitting(
      [
        `${lead}${tally}. ${season} County map, alpha-gal facts and what to do after a bite.`,
        `${lead}${tally}. ${season} County map and what to do after a bite.`,
        `${tally}. ${season} County map and bite steps.`,
        `${tally}. ${season}`,
      ],
      DESCRIPTION_MAX,
    ),
  );
}

/** Title for a county page: the place, the state's abbreviation, and the record's year. */
export function countyTitle(label: string, stateAbbr: string, year: number): string {
  // D.C. is its own county; "District of Columbia, DC" says the place twice.
  const place = stateAbbr === "DC" ? placeName(label) : `${label}, ${stateAbbr}`;
  return pageTitle(
    firstFitting(
      [
        `Lone Star Ticks in ${place}: CDC ${year} Record`,
        `Lone Star Ticks in ${place}`,
        `Lone Star Ticks: ${place}`,
      ],
      TITLE_MAX,
    ),
  );
}

/** Description for a county page: CDC's answer first, then the season, then the page. */
export function countyDescription(opts: {
  label: string;
  stateName: string;
  status: CdcCountyStatus;
  peakSeason: string;
  level: RiskLevel;
  year: number;
}): string {
  const { label, stateName: s, status, year } = opts;
  const season = opts.level === "low" ? "" : ` ${seasonSentence(opts.peakSeason)}`;
  const where = label === s ? placeName(label) : `${label}, ${s}`;
  const answer =
    status === "established"
      ? [
          `Yes. CDC lists Lone Star ticks as established in ${where} (${year}).`,
          `Yes. CDC lists Lone Star ticks as established in ${where}.`,
        ]
      : status === "reported"
        ? [
            `Yes. Lone Star ticks have been reported in ${where}, CDC's ${year} record shows.`,
            `Yes. CDC has Lone Star tick reports from ${where}.`,
          ]
        : [
            `CDC has no Lone Star tick records for ${where} (${year}). That does not mean there are none.`,
            `CDC has no Lone Star tick records for ${where}, which does not mean there are none.`,
          ];
  const tails =
    status === "no-records"
      ? [" See nearby counties and prevention steps.", ""]
      : [" See the county map and what to do after a bite.", " County map and bite steps.", ""];
  const candidates: string[] = [];
  for (const a of answer) for (const t of tails) candidates.push(`${a}${season}${t}`);
  for (const a of answer) candidates.push(a);
  return metaDescription(firstFitting(candidates, DESCRIPTION_MAX));
}
